import { createContext, useContext, type ReactNode } from "react";
import type {
  OrderPageView,
  QuotePageView,
} from "@shared-core/data/customer-link-adapter";

// Blocks a shell may place on a CIS order's or quote's page. The portal's pages render whatever the context hands them
// and know nothing of what it is: the customer build mounts no provider, so no slot is ever filled there. A shell
// that wants a block on a page supplies it with `PortalPageSlotsProvider`; the page passes it to its body's `slot`.

export interface PortalPageSlots {
  /** Placed above the order page's tabs. `reload` reads the page again, for a block whose write changed it. */
  orderPage?: (page: OrderPageView, reload: () => Promise<void>) => ReactNode;
  /** Placed above the quote page's tabs. `reload` reads the page again. */
  quotePage?: (page: QuotePageView, reload: () => Promise<void>) => ReactNode;
  /** Placed at the bottom of an order line's detail pane, by the line's `ref`. */
  orderLine?: (lineRef: string) => ReactNode;
  /** Placed beside a proof wherever the order page shows it (its card, the proof open in place, a Needs you row). */
  orderProof?: (proofId: string) => ReactNode;
  /** Placed in an artwork version's block, by its `version_id`. */
  orderVersion?: (versionId: string) => ReactNode;
  /** Placed at the bottom of a quote line's detail pane, by the line's `line_ref`. */
  quoteLine?: (lineRef: string) => ReactNode;
}

const NO_SLOTS: PortalPageSlots = {};

const PortalPageSlotsContext = createContext<PortalPageSlots>(NO_SLOTS);

export const PortalPageSlotsProvider = PortalPageSlotsContext.Provider;

export function usePortalPageSlots(): PortalPageSlots {
  return useContext(PortalPageSlotsContext);
}
